/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from 'react'

// Generate a single tab ID for the entire session
const getTabId = (() => {
  let tabId: string | null = null
  return () => {
    if (!tabId) {
      tabId = `tab-${Date.now()}-${Math.random()}`
    }
    return tabId
  }
})()

export function useBroadcastChannel<T = any>(channelName: string, ignoreCurrentTab: boolean = false) {
  const channelRef = useRef<BroadcastChannel | null>(null)
  const listenersRef = useRef<Set<(data: T) => void>>(new Set())
  const tabIdRef = useRef<string>(getTabId())

  useEffect(() => {
    channelRef.current = new BroadcastChannel(channelName)

    channelRef.current.onmessage = (event: MessageEvent<{ tabId: string; data: T }>) => {
      // Ignore messages from the current tab
      if (ignoreCurrentTab && event.data.tabId === tabIdRef.current) {
        return
      }

      listenersRef.current.forEach((listener) => listener(event.data.data))
    }

    return () => {
      channelRef.current?.close()
    }
  }, [channelName])

  const postMessage = useCallback((message: T) => {
    channelRef.current?.postMessage({
      tabId: tabIdRef.current,
      data: message,
    })
  }, [])

  const subscribe = useCallback((callback: (data: T) => void) => {
    listenersRef.current.add(callback)
    return () => {
      listenersRef.current.delete(callback)
    }
  }, [])

  return { postMessage, subscribe }
}
